import { z } from 'zod';
import { optionalString } from './OptionalString';
import { optionalNumber } from './OptionalNumber';
import { Relationship, relationshipSchema } from './Relationship';

const attachment = z.object({
  contentId: optionalString,
  contentType: optionalString,
  downloadUrl: optionalString,
  externalId: z.string(),
  filename: optionalString,
  id: z.number(),
  isInline: z.boolean().optional(),
  size: optionalNumber(),
  url: optionalString,
});

export type EmailAttachment = z.infer<typeof attachment>;

export type EmailInlineAttachment = Omit<EmailAttachment, 'isInline'> & { url: string };

const participantTypeValues = [
  'to',
  'bcc',
  'cc',
  'from',
  'reply_to',
] as const;

const participantType = z.enum(participantTypeValues);

export type EmailParticipantType = z.infer<typeof participantType>;

const participant = z.object({
  email: z.string(),
  id: z.number(),
  name: z.string(),
  type: z.array(participantType).optional(),
});

export type EmailParticipant = z.infer<typeof participant>;

const contactOpportunityOption = relationshipSchema.extend({
  accountId: z.number(),
});

export type OrphanEmailOpportunityOption = z.infer<typeof contactOpportunityOption>;

const orphanEmailContactOption = relationshipSchema.extend({
  companyName: optionalString,
  accounts: z.array(relationshipSchema).optional(),
  opportunities: z.array(contactOpportunityOption).optional(),
});

export type OrphanEmailContactOption = z.infer<typeof orphanEmailContactOption>;

export const dashboardEmail = z.object({
  attachments: z.array(attachment).optional().default(() => []),
  body: optionalString,
  direction: z.union([z.literal('incoming'), z.literal('outgoing')]),
  externalId: z.string(),
  externalReceivedAt: optionalNumber(),
  id: z.number(),
  subject: z.string(),
  snippet: optionalString,
  participants: z.object({
    bcc: z.array(participant).optional(),
    cc: z.array(participant).optional(),
    from: z.array(participant).optional(),
    // eslint-disable-next-line camelcase
    reply_to: z.array(participant).optional(),
    to: z.array(participant),
  }),
  relationships: z.object({
    contacts: z.array(orphanEmailContactOption).optional(),
  }),
});

export const dashboardEmailExclusionAddress = z.object({
  id: optionalNumber(),
  email: z.string(),
  displayName: optionalString,
});

export type DashboardEmailExclusionAddress = z.infer<typeof dashboardEmailExclusionAddress>;

export type DashboardEmail = z.infer<typeof dashboardEmail>;

export type DashboardEmailResponse = {
  emails: DashboardEmail[],
  // Base64-encoded cursor for the next page, or null on the last page. The FE passes
  // this back as ?cursor=… to load more.
  nextCursor?: string | null,
};

export type DashboardEmailInlineContentResponse = {
  body?: string | null,
  id: number,
  inlineAttachments: EmailInlineAttachment[],
  success: boolean,
};

export type DismissEmailRequest = {
  ignoreFuture?: boolean,
};

export type DismissEmailResponse = {
  status: 'dismissed' | 'excluded',
  emailStatus?: string,
};

export type UpdateRelationshipRequest = {
  relationships: Relationship[],
};

export type DashboardEmailExclusionListResponse = {
  success: boolean,
  emails: DashboardEmailExclusionAddress[],
}
